/* src/App.js
 * Main App component with routing
 */

import React, { useState, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { OnboardingGateProvider } from './contexts/OnboardingGateContext';
import { ToastProvider } from './contexts/ToastContext';

import { ProtectedRoute } from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import { useIsMobile } from './hooks/useIsMobile';
import RootRedirect from './pages/RootRedirect';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import FormBuilderList from './pages/FormBuilderList';
import FormBuilder from './pages/FormBuilder';
import PublicForm from './pages/PublicForm';
import SpamTestRedirect from './pages/SpamTestRedirect';
import Quarantine from './pages/Quarantine';
import Inbox from './pages/Inbox';
import Pipeline from './pages/Pipeline';
import Proof from './pages/Proof';
import CustomDomains from './pages/CustomDomains';
import Billing from './pages/Billing';
import TextMessaging from './pages/TextMessaging';
import TeamSettings from './pages/TeamSettings';
import AdminUserManagement from './pages/AdminUserManagement';
import VerifyEmail from './pages/VerifyEmail';
import ResetPassword from './pages/ResetPassword';
import AuthAction from './pages/AuthAction';
import AcceptInvite from './pages/AcceptInvite';
import MyProfile from './pages/MyProfile';
import Agency from './pages/Agency';
import Chat from './pages/Chat';
import KnowledgeBaseLibrary from './pages/KnowledgeBaseLibrary';
import OnboardingWizard from './pages/OnboardingWizard';
import SitesList from './pages/sites/SitesList';
import NewSiteWizard from './pages/sites/NewSiteWizard';
import SiteEditor from './pages/sites/SiteEditor';
import { SitesRoute } from './components/sites/SitesRoute';
import MarketingConsentBanner from './components/MarketingConsentBanner';
import { auth } from './firebase';

if (process.env.REACT_APP_USE_EMULATORS === 'true') {
  auth.settings.appVerificationDisabledForTesting = true;
}

/** ErrorBoundary -- catches unhandled JS errors so the app doesn't white-screen */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Uncaught error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '3rem', textAlign: 'center', maxWidth: 600, margin: '4rem auto' }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '1rem', color: 'var(--danger, #B4472F)' }}>Something went wrong</h1>
          <p style={{ color: 'var(--muted, #5B614E)', marginBottom: '1.5rem' }}>
            An unexpected error occurred. Please try refreshing the page.
          </p>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); window.location.href = '/dashboard'; }}
            style={{ padding: '0.75rem 1.5rem', background: 'var(--ink, #171A0D)', color: '#fff', border: 'none', borderRadius: '999px', cursor: 'pointer', fontSize: '1rem', fontWeight: 'bold' }}
          >
            Go to Dashboard
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/** NotFound -- 404 catch-all for invalid routes */
function NotFound() {
  return (
    <div style={{ padding: '3rem', textAlign: 'center', maxWidth: 600, margin: '4rem auto' }}>
      <h1 style={{ fontSize: '3rem', marginBottom: '0.5rem', color: 'var(--accent-ink, #5F7B12)' }}>404</h1>
      <h2 style={{ marginBottom: '1rem', color: 'var(--ink, #171A0D)' }}>Page Not Found</h2>
      <p style={{ color: 'var(--muted, #5B614E)', marginBottom: '1.5rem' }}>
        The page you're looking for doesn't exist or has been moved.
      </p>
      <Link
        to="/dashboard"
        style={{ padding: '0.75rem 1.5rem', background: 'var(--ink, #171A0D)', color: '#fff', borderRadius: '999px', textDecoration: 'none', fontWeight: 'bold', display: 'inline-block' }}
      >
        Go to Dashboard
      </Link>
    </div>
  );
}

function AppRoutes() {
  const { currentUser } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const showSidebar = currentUser && !location.pathname.startsWith('/form/') && location.pathname !== '/login' && location.pathname !== '/signup' && location.pathname !== '/agency' && location.pathname !== '/onboarding';
  const showMarketingConsent = location.pathname === '/login' || location.pathname === '/signup';

  const mainStyle = {
    marginLeft: showSidebar && !isMobile ? '232px' : 0,
    width: showSidebar && !isMobile ? 'calc(100% - 232px)' : '100%',
    position: 'relative',
  };

  return (
    <div className={showSidebar ? 'console-shell' : ''} style={{ display: 'flex' }}>
      {showSidebar && (
        <Sidebar
          mobileOpen={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
        />
      )}
      <div style={mainStyle}>
        {/* Mobile: hamburger to open sidebar */}
        {showSidebar && isMobile && (
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setMobileSidebarOpen(true)}
            style={{
              position: 'fixed',
              top: 12,
              left: 12,
              zIndex: 999,
              width: 44,
              height: 44,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--surface)',
              color: 'var(--ink)',
              border: '1px solid var(--hairline-3)',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: '1.2rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            ☰
          </button>
        )}
        {/* Spacer so page content isn't hidden under hamburger on mobile */}
        {showSidebar && isMobile && <div style={{ height: 56, minHeight: 56 }} />}
        {showMarketingConsent && <MarketingConsentBanner />}
        <Routes>
      <Route 
        path="/" 
        element={<RootRedirect />} 
      />
      <Route 
        path="/agency" 
        element={<Agency />} 
      />
      <Route 
        path="/login" 
        element={currentUser ? <Navigate to="/dashboard" replace /> : <Login />} 
      />
      <Route 
        path="/signup" 
        element={currentUser ? <Navigate to="/dashboard" replace /> : <Signup />} 
      />
      <Route 
        path="/verify-email" 
        element={<VerifyEmail />} 
      />
      <Route
        path="/reset-password"
        element={<ResetPassword />}
      />
      <Route 
        path="/__/auth/action" 
        element={<AuthAction />} 
      />
      <Route 
        path="/accept-invite" 
        element={<AcceptInvite />} 
      />
      <Route 
        path="/form/:formId" 
        element={<PublicForm />} 
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/form-builder"
        element={
          <ProtectedRoute>
            <FormBuilderList />
          </ProtectedRoute>
        }
      />
      <Route path="/forms" element={<Navigate to="/form-builder" replace />} />
      <Route
        path="/forms/new"
        element={
          <ProtectedRoute>
            <FormBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forms/:formId/edit"
        element={
          <ProtectedRoute>
            <FormBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spam-test"
        element={
          <ProtectedRoute>
            <SpamTestRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quarantine"
        element={
          <ProtectedRoute>
            <Quarantine />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Navigate to="/quarantine" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads"
        element={
          <ProtectedRoute>
            <Inbox />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/:formId"
        element={
          <ProtectedRoute>
            <Inbox />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline"
        element={
          <ProtectedRoute>
            <Pipeline />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proof"
        element={
          <ProtectedRoute>
            <Proof />
          </ProtectedRoute>
        }
      />
      <Route
        path="/custom-domains"
        element={
          <ProtectedRoute>
            <CustomDomains />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-base"
        element={
          <ProtectedRoute>
            <KnowledgeBaseLibrary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/text-messaging"
        element={
          <ProtectedRoute>
            <TextMessaging />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <Billing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/advanced-settings"
        element={
          <ProtectedRoute>
            <TeamSettings />
          </ProtectedRoute>
        }
      />
      <Route path="/team-settings" element={<Navigate to="/advanced-settings" replace />} />
      <Route 
        path="/admin/users" 
        element={
          <ProtectedRoute>
            <AdminUserManagement />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <MyProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sites"
        element={
          <SitesRoute>
            <SitesList />
          </SitesRoute>
        }
      />
      <Route
        path="/sites/new"
        element={
          <SitesRoute>
            <NewSiteWizard />
          </SitesRoute>
        }
      />
      <Route
        path="/sites/:siteId"
        element={
          <SitesRoute>
            <SiteEditor />
          </SitesRoute>
        }
      />
      {/* 404 catch-all */}
      <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <WorkspaceProvider>
            <OnboardingGateProvider>
              <ToastProvider>
                <div className="App">
                  <AppRoutes />
                </div>
              </ToastProvider>
            </OnboardingGateProvider>
          </WorkspaceProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
